/**
 * Store Zustand exclusivo do Sumário Comentado.
 * Não compartilha estado com nenhum outro store.
 */

import { create } from 'zustand';
import type {
  SCSection,
  SCBlock,
  SCBlockStyle,
  SCSectionLevel,
  SCPanelState,
  SCActivePanel,
  SCSyncStatus,
  SCSummaryDocument,
} from '../types/sumarioComentado.types';
import { safeSetItem } from '../utils/safeLocalStorage';

// ---------- localStorage ----------
const SC_PANEL_UI_KEY = 'bigwa-sumario-comentado-ui';

interface SavedUI {
  position: { x: number; y: number };
  size: { width: number; height: number };
}

function loadSavedUI(): SavedUI | null {
  try {
    const raw = localStorage.getItem(SC_PANEL_UI_KEY);
    if (raw) return JSON.parse(raw) as SavedUI;
  } catch { /* ignore */ }
  return null;
}

function saveUI(position: { x: number; y: number }, size: { width: number; height: number }): void {
  try {
    safeSetItem(SC_PANEL_UI_KEY, JSON.stringify({ position, size }));
  } catch { /* ignore */ }
}

// ---------- defaults ----------
const DEFAULT_SIZE = { width: 820, height: 640 };

function defaultPosition(): { x: number; y: number } {
  if (typeof window === 'undefined') return { x: 100, y: 80 };
  return {
    x: Math.max(60, Math.round((window.innerWidth - DEFAULT_SIZE.width) / 2)),
    y: Math.max(40, Math.round((window.innerHeight - DEFAULT_SIZE.height) / 2)),
  };
}

// ---------- cross-device sync ----------
// Timestamp of last local data mutation (DriveSync will save these changes)
let _lastMutationAt = 0;

// ---------- helpers ----------
let nextOrderCounter = 0;

function generateId(): string {
  return crypto.randomUUID();
}

// ---------- interface ----------
export interface SumarioComentadoState {
  // Panel
  panelState: SCPanelState;
  activePanel: SCActivePanel;
  hasUnseenInboxItems: boolean;

  // Data
  sections: SCSection[];
  inbox: SCBlock[];
  selectedSectionId: string | null;
  syncStatus: SCSyncStatus;
  loadedAt: number | null;

  // Panel actions
  openPanel: () => void;
  closePanel: () => void;
  togglePanel: () => void;
  updatePanelPosition: (x: number, y: number) => void;
  updatePanelSize: (width: number, height: number) => void;
  setActivePanel: (panel: SCActivePanel) => void;

  // Section actions
  addSection: (title: string) => void;
  renameSection: (sectionId: string, title: string) => void;
  setSectionLevel: (sectionId: string, level: SCSectionLevel) => void;
  deleteSection: (sectionId: string) => void;
  deleteSectionPermanently: (sectionId: string) => void;
  reorderSections: (orderedIds: string[]) => void;
  updateSectionFreeText: (sectionId: string, text: string) => void;

  // Selection
  setSelectedSectionId: (id: string | null) => void;
  getSelectedOrFirstSectionId: () => string | null;

  // Block actions
  addFreeTextBlock: (sectionId: string) => void;
  updateBlockStyle: (sectionId: string, blockId: string, style: SCBlockStyle) => void;
  removeBlockFromSection: (sectionId: string, blockId: string) => void;
  returnBlockToInbox: (sectionId: string, blockId: string) => void;
  reorderBlocksInSection: (sectionId: string, orderedBlockIds: string[]) => void;
  moveBlockToSection: (fromSectionId: string, blockId: string, toSectionId: string, insertIndex?: number) => void;
  addToInbox: (block: SCBlock) => void;
  moveToSection: (blockId: string, sectionId: string) => void;
  removeFromInbox: (blockId: string) => void;

  // Persistence
  loadFromDrive: (doc: SCSummaryDocument | null) => void;
  getDocument: () => SCSummaryDocument;
  setSyncStatus: (status: SCSyncStatus) => void;

  // Cross-device sync
  revalidateIfStale: () => void;

  // Metrics
  getTotalBlocks: () => number;
  getTotalWords: () => number;
  getInboxCount: () => number;
}

// ---------- store ----------
export const useSumarioComentadoStore = create<SumarioComentadoState>((set, get) => {
  const savedUI = loadSavedUI();

  return {
    // ─── Panel State ───
    panelState: {
      isOpen: false,
      position: savedUI?.position ?? defaultPosition(),
      size: savedUI?.size ?? DEFAULT_SIZE,
    },
    activePanel: null,

    hasUnseenInboxItems: false,

    // ─── Data ───
    sections: [],
    inbox: [],
    selectedSectionId: null,
    syncStatus: 'saved',
    loadedAt: null,

    // ─── Panel Actions ───
    openPanel: () => {
      const { panelState } = get();
      if (panelState.isOpen) return;
      set({
        panelState: { ...panelState, isOpen: true },
        hasUnseenInboxItems: false,
      });
    },

    closePanel: () => {
      const { panelState } = get();
      if (!panelState.isOpen) return;
      set({
        panelState: { ...panelState, isOpen: false },
        activePanel: null,
      });
    },

    togglePanel: () => {
      const { panelState } = get();
      if (panelState.isOpen) {
        get().closePanel();
      } else {
        get().openPanel();
      }
    },

    updatePanelPosition: (x: number, y: number) => {
      const { panelState } = get();
      const newState = { ...panelState, position: { x, y } };
      set({ panelState: newState });
      saveUI(newState.position, newState.size);
    },

    updatePanelSize: (width: number, height: number) => {
      const { panelState } = get();
      const newState = { ...panelState, size: { width, height } };
      set({ panelState: newState });
      saveUI(newState.position, newState.size);
    },

    setActivePanel: (panel: SCActivePanel) => {
      set({ activePanel: panel });
    },

    // ─── Section Actions ───
    addSection: (title: string) => {
      const { sections } = get();
      const newSection: SCSection = {
        id: generateId(),
        title,
        level: 'h2',
        order: nextOrderCounter++,
        blocks: [],
        freeText: '',
      };
      set({ sections: [...sections, newSection] });
      _lastMutationAt = Date.now();
    },

    renameSection: (sectionId: string, title: string) => {
      const { sections } = get();
      set({
        sections: sections.map((s) =>
          s.id === sectionId ? { ...s, title } : s
        ),
      });
      _lastMutationAt = Date.now();
    },

    setSectionLevel: (sectionId: string, level: SCSectionLevel) => {
      const { sections } = get();
      set({
        sections: sections.map((s) =>
          s.id === sectionId ? { ...s, level } : s
        ),
      });
      _lastMutationAt = Date.now();
    },

    deleteSection: (sectionId: string) => {
      const { sections, inbox } = get();
      const section = sections.find((s) => s.id === sectionId);
      // Return blocks to inbox when section is deleted
      const returnedBlocks = section ? section.blocks : [];
      set({
        sections: sections
          .filter((s) => s.id !== sectionId)
          .map((s, i) => ({ ...s, order: i })),
        inbox: [...inbox, ...returnedBlocks],
      });
      _lastMutationAt = Date.now();
    },

    // Delete section permanently — blocks are lost (no return to inbox)
    deleteSectionPermanently: (sectionId: string) => {
      const { sections } = get();
      set({
        sections: sections
          .filter((s) => s.id !== sectionId)
          .map((s, i) => ({ ...s, order: i })),
      });
      _lastMutationAt = Date.now();
    },

    reorderSections: (orderedIds: string[]) => {
      const { sections } = get();
      const sectionMap = new Map(sections.map((s) => [s.id, s]));
      const reordered = orderedIds
        .map((id, i) => {
          const section = sectionMap.get(id);
          return section ? { ...section, order: i } : null;
        })
        .filter((s): s is SCSection => s !== null);
      set({ sections: reordered });
      _lastMutationAt = Date.now();
    },

    updateSectionFreeText: (sectionId: string, text: string) => {
      const { sections } = get();
      set({
        sections: sections.map((s) =>
          s.id === sectionId ? { ...s, freeText: text } : s
        ),
      });
      _lastMutationAt = Date.now();
    },

    // ─── Selection ───
    setSelectedSectionId: (id: string | null) => {
      set({ selectedSectionId: id });
    },

    getSelectedOrFirstSectionId: () => {
      const { selectedSectionId, sections } = get();
      if (selectedSectionId && sections.some((s) => s.id === selectedSectionId)) {
        return selectedSectionId;
      }
      return sections.length > 0 ? sections[0].id : null;
    },

    // ─── Block Actions ───
    addFreeTextBlock: (sectionId: string) => {
      const { sections } = get();
      const newBlock: SCBlock = {
        id: generateId(),
        type: 'freetext',
        text: '',
        style: 'body',
      };
      set({
        sections: sections.map((s) =>
          s.id === sectionId
            ? { ...s, blocks: [...s.blocks, newBlock] }
            : s
        ),
      });
      _lastMutationAt = Date.now();
    },

    updateBlockStyle: (sectionId: string, blockId: string, style: SCBlockStyle) => {
      const { sections } = get();
      set({
        sections: sections.map((s) =>
          s.id === sectionId
            ? {
                ...s,
                blocks: s.blocks.map((b) =>
                  b.id === blockId ? { ...b, style } : b
                ),
              }
            : s
        ),
      });
      _lastMutationAt = Date.now();
    },

    // Remove a block from a section permanently (no return to inbox)
    removeBlockFromSection: (sectionId: string, blockId: string) => {
      const { sections } = get();
      set({
        sections: sections.map((s) =>
          s.id === sectionId
            ? { ...s, blocks: s.blocks.filter((b) => b.id !== blockId) }
            : s
        ),
      });
      _lastMutationAt = Date.now();
    },

    // Remove a block from a section and return it to the inbox
    returnBlockToInbox: (sectionId: string, blockId: string) => {
      const { sections, inbox } = get();
      const section = sections.find((s) => s.id === sectionId);
      const block = section?.blocks.find((b) => b.id === blockId);
      if (!block) return;
      set({
        sections: sections.map((s) =>
          s.id === sectionId
            ? { ...s, blocks: s.blocks.filter((b) => b.id !== blockId) }
            : s
        ),
        inbox: [...inbox, block],
      });
      _lastMutationAt = Date.now();
    },

    reorderBlocksInSection: (sectionId: string, orderedBlockIds: string[]) => {
      const { sections } = get();
      set({
        sections: sections.map((s) => {
          if (s.id !== sectionId) return s;
          const blockMap = new Map(s.blocks.map(b => [b.id, b]));
          const reordered = orderedBlockIds
            .map(id => blockMap.get(id))
            .filter((b): b is SCBlock => b !== undefined);
          return { ...s, blocks: reordered };
        }),
      });
      _lastMutationAt = Date.now();
    },

    moveBlockToSection: (fromSectionId: string, blockId: string, toSectionId: string, insertIndex?: number) => {
      const { sections } = get();
      const fromSection = sections.find(s => s.id === fromSectionId);
      const block = fromSection?.blocks.find(b => b.id === blockId);
      if (!block) return;
      set({
        sections: sections.map((s) => {
          if (s.id === fromSectionId) {
            return { ...s, blocks: s.blocks.filter(b => b.id !== blockId) };
          }
          if (s.id === toSectionId) {
            const newBlocks = [...s.blocks];
            const idx = insertIndex !== undefined ? insertIndex : newBlocks.length;
            newBlocks.splice(idx, 0, block);
            return { ...s, blocks: newBlocks };
          }
          return s;
        }),
      });
      _lastMutationAt = Date.now();
    },

    addToInbox: (block: SCBlock) => {
      const { inbox, panelState } = get();
      // Prevent duplicates
      if (inbox.some((b) => b.id === block.id)) return;
      // Ensure block has an id
      const blockWithId: SCBlock = block.id ? block : { ...block, id: generateId() };
      set({
        inbox: [...inbox, blockWithId],
        // Pulse the bottom-bar icon when panel is closed
        ...(!panelState.isOpen ? { hasUnseenInboxItems: true } : {}),
      });
      _lastMutationAt = Date.now();
    },

    moveToSection: (blockId: string, sectionId: string) => {
      const { inbox, sections } = get();
      const block = inbox.find((b) => b.id === blockId);
      if (!block) return;
      set({
        inbox: inbox.filter((b) => b.id !== blockId),
        sections: sections.map((s) =>
          s.id === sectionId
            ? { ...s, blocks: [...s.blocks, block] }
            : s
        ),
      });
      _lastMutationAt = Date.now();
    },

    removeFromInbox: (blockId: string) => {
      const { inbox } = get();
      set({ inbox: inbox.filter((b) => b.id !== blockId) });
      _lastMutationAt = Date.now();
    },

    // ─── Persistence ───
    loadFromDrive: (doc: SCSummaryDocument | null) => {
      if (!doc) {
        // No data — start fresh
        set({ sections: [], inbox: [], syncStatus: 'saved', loadedAt: Date.now() });
        nextOrderCounter = 0;
        return;
      }
      const sections = doc.sections || [];
      const inbox = doc.inbox || [];
      // Restore order counter
      nextOrderCounter = sections.reduce((max, s) => Math.max(max, s.order + 1), 0);
      set({ sections, inbox, syncStatus: 'saved', loadedAt: Date.now() });
    },

    getDocument: (): SCSummaryDocument => {
      const { sections, inbox } = get();
      return {
        version: 1,
        lastModified: new Date().toISOString(),
        sections,
        inbox,
      };
    },

    setSyncStatus: (status: SCSyncStatus) => {
      set({ syncStatus: status });
    },

    // ─── Cross-device sync ───
    revalidateIfStale: () => {
      const { loadedAt, syncStatus } = get();
      if (!loadedAt) return;
      if (Date.now() - loadedAt < 300_000) return;
      // Local mutation within last 10s — DriveSync may not have flushed yet
      if (_lastMutationAt && Date.now() - _lastMutationAt < 10_000) return;
      // If currently syncing, skip
      if (syncStatus === 'saving') return;
      // Signal that data is stale — DriveSync will handle the actual reload
      set({ loadedAt: null });
    },

    // ─── Metrics ───
    getTotalBlocks: () => {
      const { sections, inbox } = get();
      return sections.reduce((acc, s) => acc + s.blocks.length, 0) + inbox.length;
    },

    getTotalWords: () => {
      const { sections, inbox } = get();
      const sectionWords = sections.reduce((acc, s) => {
        const blockWords = s.blocks.reduce(
          (w, b) => w + (b.text ? b.text.split(/\s+/).filter(Boolean).length : 0),
          0
        );
        const freeWords = s.freeText
          ? s.freeText.split(/\s+/).filter(Boolean).length
          : 0;
        return acc + blockWords + freeWords;
      }, 0);
      const inboxWords = inbox.reduce(
        (w, b) => w + (b.text ? b.text.split(/\s+/).filter(Boolean).length : 0),
        0
      );
      return sectionWords + inboxWords;
    },

    getInboxCount: () => {
      return get().inbox.length;
    },
  };
});
